import React, { useContext, useEffect, useState } from "react";

import {
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
} from "@mui/material";
import { Alert } from "@mui/material";

import { SettingValue } from "@safetyculture/s12-apis-connect-web/s12/accounts/settings/v1/settings_pb";
import { IndustryVertical } from "@safetyculture/s12-apis-connect-web/s12/accounts/v1/industry_vertical_pb";
import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";

import { updateOrgIndustryVertical } from "api/endpoints/organisation";
import { callOptionsHeaders } from "api/helpers";
import { DEFAULT_SNACKBAR_DURATION } from "constants/index";
import {
  industryVerticalByStoredName,
  industryVerticals,
} from "constants/industry-vertical";
import { PermissionsContext } from "context/permissions-context";
import { SnackbarContext } from "context/snackbar-context";
import { getEnvironment } from "helpers/environment";

const INDUSTRY_VERTICAL_SETTING_KEY = "organization.industry_vertical";
const UNSET_OPTION_VALUE = "";

export interface OrgIndustryVerticalProps {
  regionCode: string;
  orgId: string;
  orgSettings: SettingValue[];
}

const getInitialSelectValue = (orgSettings: SettingValue[]): string => {
  const setting = orgSettings.find(
    (s) => s.key === INDUSTRY_VERTICAL_SETTING_KEY,
  );
  if (setting?.value?.value.case !== "stringValue") {
    return UNSET_OPTION_VALUE;
  }

  const enumValue = industryVerticalByStoredName[setting.value.value.value];
  if (enumValue === undefined || enumValue === IndustryVertical.UNSPECIFIED) {
    return UNSET_OPTION_VALUE;
  }

  return String(enumValue);
};

const OrgIndustryVertical = ({
  regionCode,
  orgId,
  orgSettings,
}: OrgIndustryVerticalProps) => {
  const { hasPermissions, permissions } = useContext(PermissionsContext);
  const { showError } = useContext(SnackbarContext);
  const [value, setValue] = useState(() => getInitialSelectValue(orgSettings));
  const [saving, setSaving] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    setValue(getInitialSelectValue(orgSettings));
  }, [orgSettings]);

  const handleChange = (event: SelectChangeEvent) => {
    const newValue = event.target.value;
    setSaving(true);

    const params =
      newValue === UNSET_OPTION_VALUE
        ? { orgId, environment: getEnvironment(), clear: true }
        : {
            orgId,
            environment: getEnvironment(),
            industryVertical: Number(newValue) as IndustryVertical,
          };

    updateOrgIndustryVertical(params, callOptionsHeaders(regionCode, orgId))
      .then(() => {
        setValue(newValue);
        setSnackbarOpen(true);
      })
      .catch(showError)
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <>
      <FormControl fullWidth size="small">
        <Select
          data-testid="org-industry-vertical-select"
          value={value}
          disabled={
            saving ||
            permissions.isPermissionsLoading ||
            !hasPermissions([ActionType.UPDATE_ORG_INDUSTRY_VERTICAL])
          }
          onChange={handleChange}
          displayEmpty
          inputProps={{
            "data-testid": "org-industry-vertical-input",
          }}
        >
          <MenuItem value={UNSET_OPTION_VALUE}>---</MenuItem>
          {industryVerticals.map((option) => (
            <MenuItem key={option.value} value={String(option.value)}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={snackbarOpen}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={() => setSnackbarOpen(false)}
        data-testid="snackbar-change-industry-vertical"
      >
        <Alert severity="success" variant="filled">
          Industry vertical updated successfully
        </Alert>
      </Snackbar>
    </>
  );
};

export default OrgIndustryVertical;
